import React, {useEffect, useState} from 'react';
import styled, {css, useTheme} from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {tr} from '../../../translation';
import GenericInput from '../../../components/textInput';
import GenericButton from '../../../components/button';
import Text from '../../../components/text';
import {Theme} from '../../../theme/lightTheme';
import {
  checkIfEmailValid,
  checkIfFormValid,
  handleChange,
} from '../../../utils/form';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {RegisterForm} from '../../../interfaces/api/auth/register';
import {register} from '../login/actions';
import DatePicker from '../../../components/datePicker';
import Picker from '../../../components/picker';
import Paths from '../../../config/paths';
import useScrollTop from '../../../utils/hooks/useScrollTop';
import {useSetMainLayoutProps} from '../../../route/routeMainLayout/context';
import {SpecialityType} from '../../../interfaces/api/auth/login';
import {getSpecialities} from '../../content/home/actions';
import {motion} from 'framer-motion';
import {LoginImage, LoginImageContainer} from '../login';

const Title = styled.p`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7};
  font-weight: 400;
  text-align: center;
  letter-spacing: 1px;
  padding-bottom: 10%;
  margin: 0;
`;

const LinkText = styled.a`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s10};
  font-weight: 100;
  text-align: center;
  letter-spacing: 1px;
  padding-top: 5%;
  padding-bottom: 5%;
  text-decoration: underline;
  cursor: pointer;
`;

const LogoContainer = styled(LoginImageContainer)`
  margin-bottom: 20px;
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const Container = styled.div`
  display: inline-block;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
`;
const ContentContainer = styled.div<{fromDrawer: boolean}>`
  overflow-y: ${({fromDrawer}) => (fromDrawer ? `auto` : 'hidden')};
  display: inline-block;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const FormContainer = styled.div<{fromDrawer?: boolean}>`
  filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2));
  backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  flex-direction: column;
  width: ${({theme, fromDrawer}) =>
    theme?.isMobile ? '90%' : fromDrawer ? '77%' : '60%'};
  padding: 4%;
  margin: 6% auto;
  background: linear-gradient(
    360deg,
    ${({theme}) => theme.secondary + '11'} -68.52%,
    ${({theme}) => theme.primary + '11'} 122.02%
  );
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 26px;

  ${({fromDrawer}) =>
    !fromDrawer &&
    css`
      filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2));
      backdrop-filter: blur(16px);
    `};
`;
const Name = styled.p<{$fromDrawer?: boolean}>`
  color: ${({theme, $fromDrawer}) =>
    $fromDrawer ? theme.text.light : theme.text.dark};
  font-size: ${({theme}) => theme.text.s8};
  text-align: center;
  letter-spacing: 1px;
  padding: 5%;
  text-transform: capitalize;
`;
const TopContainer = styled.div`
  width: 100%;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;
  padding: 5% 0;
`;
const ProfileImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: ${({theme}: {theme: Theme}) => (theme.isMobile ? 5 : 7)}rem;
  aspect-ratio: 1;
  overflow: hidden;
  border-color: ${({theme}) => theme.homeBackground};
  border-width: 1px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  opacity: 0.85;
`;
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 5%;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
`;
const InputWrapper = styled(motion.div)`
  display: flex;
  width: 100%;
  padding-bottom: 30px;
  @media (min-width: 768px) {
    width: 48%;
  }
`;

// Define the mandatory fields for the form
const mandatoryFields = [
  'email',
  'password',
  'confirmPassword',
  'firstName',
  'lastName',
  'bod',
  'gender',
  'country',
  'profession',
  'specialty',
];

const Register = () => {
  useSetMainLayoutProps({
    showHeader: false,
    showBackgroundImage: false,
    showBack: true,
    showDrawer: false,
  });
  const theme = useTheme() as Theme;
  const navigation = useNavigate();
  useScrollTop();
  const {Countries, Genders, Languages, Professions} =
    useSelector((state: RootState) => state.auth)?.config || {};
  const {specialties} = useSelector((state: RootState) => state.homeTabs) || {};

  // Define the form state
  const [form, updateForm] = useState<any>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [isErrorEmail, setIsErrorEmail] = useState(false);
  // Define the form completion state
  const [formComplete, setFormComplete] = useState(false);
  // Define the error state
  const [isError, setIsError] = useState(false);
  const [localSpecialties, setLocalSpecialties] = useState<SpecialityType[]>(
    specialties || [],
  );
  const [isSpecialtiesUpdated, setIsSpecialtiesUpdated] = useState(false);

  // Effect to check if the form is complete
  useEffect(() => {
    const isValidEmail = checkIfEmailValid(form.email);
    setFormComplete(checkIfFormValid(form, mandatoryFields) && isValidEmail);
    setIsErrorEmail(!isValidEmail);
  }, [form]);

  const updateSpecialities = async () => {
    if (form?.profession?.[0]?.id) {
      const res = await getSpecialities(form?.profession?.[0]?.id);
      setLocalSpecialties(res);
      if (res?.length === 1) {
        handleChange('specialty', res[0], updateForm);
      } else {
        handleChange('specialty', null, updateForm);
      }
    }
  };

  useEffect(() => {
    if (form?.profession?.[0]?.id) {
      setIsSpecialtiesUpdated(true);
      updateSpecialities();
    }
  }, [form.profession]);

  // Function to handle form submission
  const submit = async () => {
    setIsError(false);
    const {
      email,
      password,
      firstName,
      lastName,
      bod,
      gender,
      country,
      profession,
      specialty,
      language,
    } = form;
    const body: RegisterForm = {
      email: email,
      password: password,
      first_name: firstName,
      last_name: lastName,
      date_of_birth: bod,
      gender: gender[0].id,
      country_id: country[0].id,
      profession_id: profession[0].id,
      speciality_id: Array.isArray(specialty)
        ? specialty?.[0]?.id
        : specialty?.id,
      language_id: language?.id || 2, //make english id is the default
    };

    const res = await register(body);
    setIsError(!res);
  };

  const _getPickerList = (label: string): any[] => {
    switch (label) {
      case 'gender':
        return Genders || [];
      case 'language':
        return Languages || [];
      case 'profession':
        return Professions || [];
      case 'specialty':
        return localSpecialties || [];
      case 'country':
        return Countries || [];
      default:
        return [];
    }
  };

  // Function to handle "Login" button press
  const loginPressHandler = () => {
    navigation(Paths.auth.login);
  };

  const _renderItem = (label: string) => {
    if (label === 'bod') {
      return (
        <InputWrapper key={label}>
          <DatePicker
            label={tr(`register.${label}`)}
            labelStyle={{
              fontWeight: '500',
              color: theme.text.dark,
            }}
            placeholder={tr(`register.${label}`)}
            value={form[label]}
            onChange={date => handleChange(label, date, updateForm)}
            maxDate={new Date()}
            format={'YYYY-MM-DD'}
          />
        </InputWrapper>
      );
    }
    if (
      label === 'gender' ||
      label === 'language' ||
      label === 'profession' ||
      label === 'country' ||
      label === 'specialty'
    ) {
      return (
        <InputWrapper key={label}>
          <Picker
            key={`${label}-${label === 'specialty' ? form?.[label]?.id || '' : ''}`}
            placeholder={tr(`register.${label}`)}
            label={tr(`register.${label}`)}
            labelStyle={{
              fontWeight: '500',
              color: theme.text.dark,
            }}
            list={_getPickerList(label)}
            labelKey={'name'}
            valueKey={'id'}
            defaultValue={form?.[label]?.name || ''}
            values={form?.[label]?.name ? [form?.[label]] : []}
            onChange={values => handleChange(label, values, updateForm)}
          />
        </InputWrapper>
      );
    }
    return (
      <InputWrapper key={label}>
        <GenericInput
          placeholder={tr(`register.${label}`)}
          label={tr(`register.${label}`)}
          labelStyle={{
            fontWeight: '500',
            color: theme.text.dark,
          }}
          style={{
            color: theme.text.dark,
          }}
          value={form[label] || ''}
          onChange={e => handleChange(label, e.target.value, updateForm)}
          secureInput={label.toLowerCase().includes('password')}
          hasError={form?.password !== form?.confirmPassword}
          enableErrorMessage={
            label === 'confirmPassword' &&
            form.confirmPassword.length > 0 &&
            form.password.length > 0
          }
          errorMessage={'Passwords do not match.'}
          showRightIcon={label === 'email' && form.email.length > 0}
          rightIconType={'SVG'}
          rightIconName={isErrorEmail ? 'errorField' : 'checkCircle'}
          rightIconColor={
            label.toLowerCase().includes('password')
              ? theme.text.dark
              : undefined
          }
        />
      </InputWrapper>
    );
  };

  return (
    <Container>
      <FormContainer>
        <LogoContainer>
          <LoginImage
            src={require('../../../assets/images/onboarding/onboard-1.png')}
          />
        </LogoContainer>
        <Title>{tr('app.slogan')}</Title>
        <InputContainer>{mandatoryFields.map(_renderItem)}</InputContainer>

        <GenericButton
          disabled={!formComplete}
          onPress={submit}
          label={tr('login.signIn')}
          style={{marginBottom: '5%', padding: '0 5%'}}
        />
        <Text
          style={{
            color: theme.text.dark,
            fontSize: theme.text.s8,
            marginTop: '5%',
          }}>
          {tr('register.loginPart1')}{' '}
          <LinkText
            onClick={loginPressHandler}
            style={{
              fontSize: theme.text.s8,
              fontWeight: 'bold',
            }}>
            {tr('register.loginPart2')}
          </LinkText>
        </Text>
      </FormContainer>
    </Container>
  );
};

export default Register;
